import {
  STATUS_LABELS,
  TICKET_STATUS_WORKFLOW_ORDER,
  isRetiredTicketStatus,
  statusToSlug,
} from "@sd/constants/ticketStatuses";

/** Tracker row labels (Issues by Tracker matrix). */
export const TRACKER_REPORT_ROWS = [
  { id: "bug", label: "Bug" },
  { id: "new_requirement", label: "New Requirement" },
  { id: "suggestion", label: "Suggestion" },
] as const;

export type TrackerReportRowId = (typeof TRACKER_REPORT_ROWS)[number]["id"];

/** Reports API / ticket list `tracker` filter value for a matrix row. */
export function trackerRowToApiFilter(rowId: TrackerReportRowId): string {
  return TRACKER_REPORT_ROWS.find(row => row.id === rowId)?.label ?? rowId;
}

/** Same workflow order as Kanban / status dropdowns / filters. */
export const TRACKER_STATUS_ORDER = TICKET_STATUS_WORKFLOW_ORDER;

export type TrackerReportStatusId =
  (typeof TICKET_STATUS_WORKFLOW_ORDER)[number];

export const TRACKER_REPORT_STATUS_COLUMNS = TRACKER_STATUS_ORDER.map(id => ({
  id,
  label: STATUS_LABELS[id],
}));

const TRACKER_ROW_LABEL_TO_ID = new Map(
  TRACKER_REPORT_ROWS.map(row => [row.label.toLowerCase(), row.id])
);

const TRACKER_STATUS_LABEL_TO_ID = new Map(
  TRACKER_REPORT_STATUS_COLUMNS.map(col => [col.label.toLowerCase(), col.id])
);

const TRACKER_STATUS_SLUG_TO_ID = new Map(
  TRACKER_REPORT_STATUS_COLUMNS.map(col => [col.id, col.id])
);

/**
 * API statuses not shown as columns roll into a workflow column.
 * Retired statuses (e.g. closed) are not displayed and are not aggregated
 * into the workflow-column matrix. `pending_from_client` is a normal column.
 */
const STATUS_AGGREGATE_INTO: Partial<Record<string, TrackerReportStatusId>> = {};

const CLOSED_STATUS_IDS = new Set<TrackerReportStatusId>([
  "moved_to_production",
  "close",
]);

export function isTrackerOpenStatus(statusId: TrackerReportStatusId): boolean {
  return !CLOSED_STATUS_IDS.has(statusId);
}

export function trackerClosedStatusIds(): TrackerReportStatusId[] {
  return TRACKER_REPORT_STATUS_COLUMNS.filter(
    col => !isTrackerOpenStatus(col.id)
  ).map(col => col.id);
}

export function resolveTrackerRowId(raw: unknown): TrackerReportRowId | null {
  const text = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (!text) return null;

  const byLabel = TRACKER_ROW_LABEL_TO_ID.get(text.replace(/_/g, " "));
  if (byLabel) return byLabel;

  if (text === "bug" || text === "issue" || text.includes("bug")) {
    return "bug";
  }
  if (text === "new_requirement" || text.includes("requirement")) {
    return "new_requirement";
  }
  if (text === "suggestion") return "suggestion";

  const direct = TRACKER_REPORT_ROWS.find(row => row.id === text);
  if (direct) return direct.id;

  return null;
}

/**
 * Map an API status key/label to a report column.
 * Only exact workflow columns (and explicit STATUS_AGGREGATE_INTO rollups).
 * Never alias retired keys (e.g. `fixed`) or unknown statuses into another column.
 */
export function resolveTrackerStatusId(
  raw: unknown
): TrackerReportStatusId | null {
  if (raw === null || raw === undefined) return null;

  const label = String(raw).trim().toLowerCase();
  const byLabel = TRACKER_STATUS_LABEL_TO_ID.get(label);
  if (byLabel) return byLabel;

  const slug = statusToSlug(raw);
  if (!slug) return null;

  // API may still send retired keys (e.g. `fixed: 0`) — ignore, do not remap.
  if (isRetiredTicketStatus(slug)) return null;

  const direct = TRACKER_STATUS_SLUG_TO_ID.get(slug as TrackerReportStatusId);
  if (direct) return direct;

  const aggregated = STATUS_AGGREGATE_INTO[slug];
  if (aggregated) return aggregated;

  return null;
}
